import { queryOptions } from "@tanstack/react-query";
import {
  GET_POINT,
  GET_RTM_LOGIN_AUTH,
  GET_UNCOMPLETED_LOVENSE_LOG_BY_ROOM,
  PAY_SECOND_LIVE_POINT,
  PAY_SECOND_VIDEO_CALL_POINT,
  SECOND_APPS_GET_LIVE_CHANNELS,
  SEND_MESSAGE_FROM_WEB,
} from "~/constants/apiEndpoints";
import { apiClient } from "~/lib/apiClient";
import { getAvatarUrl, getLiveThumbnailUrl } from "~/lib/imageUrl";
import { queryClient } from "~/lib/queryClient";
import { queryKeys } from "~/lib/queryKeys";
import type {
  GetPointResponse,
  GetUncompletedLovenseLogResponse,
  LiveChannel,
  LiveChannelsResponse,
  PayLiveBillingRequest,
  PayLiveBillingResponse,
  PayLivePointRequest,
  PayLivePointResponse,
  RtmLoginAuthResponse,
  SecondAppsLiveChannelsApiResponse,
} from "./types";

const toChannel = (
  raw: SecondAppsLiveChannelsApiResponse["in_live_list"][number],
  isStandby: boolean,
): LiveChannel => {
  const thumbnailId =
    raw.channel_info.custom_thumbnail_id ||
    raw.channel_info.thumbnail_image_id ||
    null;
  // recording_id を優先し、なければ buzz_live_recording_id を buzz_file_id として使用
  const recordingId = raw.channel_info.recording_id || null;
  const buzzFileId =
    !recordingId && raw.broadcaster.buzz_live_recording_id
      ? raw.broadcaster.buzz_live_recording_id
      : null;
  return {
    channel_id: raw.channel_info.channel_id,
    broadcaster_id: raw.broadcaster.user_id,
    broadcaster_name: raw.broadcaster.user_name,
    broadcaster_avatar: getAvatarUrl(raw.broadcaster.ava_id),
    app_id: raw.channel_info.app_id,
    rtc_channel_token: raw.channel_info.rtc_channel_token,
    rtm_channel_token: raw.channel_info.rtm_channel_token || null,
    viewer_count: raw.channel_info.user_count ?? 0,
    point_per_minute: null,
    thumbnail_url: thumbnailId ? getLiveThumbnailUrl(thumbnailId) : null,
    recording_id: recordingId,
    buzz_file_id: buzzFileId,
    has_lovense: raw.broadcaster.has_lovense ?? false,
    is_standby: isStandby,
    broadcaster_age: raw.broadcaster.age,
    broadcaster_region: raw.broadcaster.region,
    abt: raw.broadcaster.abt,
    bust_size: raw.broadcaster.bust_size,
    average_score: raw.broadcaster.average_score,
    review_count: raw.broadcaster.review_count,
  };
};

export const liveChannelsQueryOptions = () => {
  return queryOptions({
    queryKey: queryKeys.live.channels(),
    queryFn: async () => {
      const raw = await apiClient<SecondAppsLiveChannelsApiResponse>(
        SECOND_APPS_GET_LIVE_CHANNELS,
        { call_type: "live" },
      );
      const result = {
        in_live_list: (raw.in_live_list ?? []).map((ch) =>
          toChannel(ch, false),
        ),
        standby_list: (raw.standby_list ?? []).map((ch) => toChannel(ch, true)),
      } satisfies LiveChannelsResponse;

      return result;
    },
    staleTime: 0,
    refetchInterval: 5_000,
  });
};

export const getRtmLoginAuthMutationOptions = () => {
  return {
    mutationFn: () => apiClient<RtmLoginAuthResponse>(GET_RTM_LOGIN_AUTH),
  };
};

export const getPointQueryOptions = () => {
  return queryOptions({
    queryKey: queryKeys.user.point(),
    queryFn: () => apiClient<GetPointResponse>(GET_POINT),
    staleTime: 0,
  });
};

export const payLivePointMutationOptions = () => {
  return {
    mutationFn: (params: PayLivePointRequest) =>
      apiClient<PayLivePointResponse>(PAY_SECOND_LIVE_POINT, {
        broadcaster_id: params.broadcaster_id,
        duration: params.duration,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.myProfile.detail() });
      queryClient.invalidateQueries({ queryKey: queryKeys.user.point() });
    },
  };
};

export const payLiveBillingMutationOptions = () => {
  return {
    mutationFn: (params: PayLiveBillingRequest) =>
      apiClient<PayLiveBillingResponse>(PAY_SECOND_VIDEO_CALL_POINT, {
        partner_id: params.broadcaster_id,
        duration: params.duration,
        web_custom_consume_point_per_minute: 0,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.myProfile.detail() });
      queryClient.invalidateQueries({ queryKey: queryKeys.user.point() });
    },
  };
};

/**
 * ライブ視聴後に配信者へギフト（スタンプギフト）を送付する。
 * 通話後ギフトと同じ send_message_from_web 経路（is_sticker / is_gift）。
 * content は `${categoryId}_${name}` 形式。
 */
export const sendGiftMessageMutationOptions = () => {
  return {
    mutationFn: (params: { userId: string; rcvId: string; content: string }) =>
      apiClient<unknown>(SEND_MESSAGE_FROM_WEB, {
        user_id: params.userId,
        rcv_id: params.rcvId,
        content: params.content,
        is_sticker: true,
        is_gift: true,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.myProfile.detail() });
      queryClient.invalidateQueries({ queryKey: queryKeys.user.point() });
    },
  };
};

export const uncompletedLovenseLogQueryOptions = (
  broadcasterId: string,
  enabled: boolean,
) => {
  return queryOptions({
    queryKey: queryKeys.live.uncompletedLog(broadcasterId),
    queryFn: () =>
      apiClient<GetUncompletedLovenseLogResponse>(
        GET_UNCOMPLETED_LOVENSE_LOG_BY_ROOM,
        { female_id: broadcasterId },
      ),
    enabled,
    staleTime: 0,
    refetchInterval: 1000,
  });
};
